import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { waitFor, onMount } from 'lp-hoc'
import { Modal, LoadingSpinner } from 'components'
import ProgramBlade from '../../profile/components/program-blade'
import { ButtonArea } from 'lp-components'
import { selectors as globalSelectors } from 'global-reducer'
import * as apiActions from 'api-actions'
import * as Types from 'types'
const propTypes = {
  programs: PropTypes.arrayOf(PropTypes.object).isRequired,
  onProgramsChange: PropTypes.func.isRequired,
  /** Called with the current programs list when the user submits the application. */
  onSubmitApplication: PropTypes.func.isRequired,
  grades: PropTypes.arrayOf(Types.grade).isRequired,
  programTypes: PropTypes.arrayOf(Types.programType).isRequired,
  outcomeTypes: PropTypes.arrayOf(Types.outcomeType).isRequired,
  approaches: PropTypes.arrayOf(Types.communityEnumerable).isRequired,
  disciplines: PropTypes.arrayOf(Types.discipline).isRequired,
  languages: PropTypes.arrayOf(Types.language).isRequired,
  applicationSubmitting: PropTypes.bool,
}

const defaultProps = {
  applicationSubmitting: false,
}

function ApplicationFormStep3({
  programs,
  onProgramsChange,
  onSubmitApplication,
  grades,
  programTypes,
  outcomeTypes,
  approaches,
  disciplines,
  languages,
  applicationSubmitting,
}) {
  const [submitPending, setSubmitPending] = useState(false)
  const [showAddProgramModal, setShowAddProgramModal] = useState(false)
  const [modalInstanceKey, setModalInstanceKey] = useState(0)

  const openAddModal = useCallback(() => {
    setModalInstanceKey((k) => k + 1)
    setShowAddProgramModal(true)
  }, [])

  const closeAddModal = useCallback(() => {
    setShowAddProgramModal(false)
  }, [])

  const handleAddProgram = useCallback(
    (params) => {
      const clientId = `draft-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 9)}`
      const id = Date.now()
      onProgramsChange((prev) => [...prev, { ...params, clientId, id }])
      return Promise.resolve()
    },
    [onProgramsChange]
  )

  const handleUpdateProgram = useCallback(
    (program) => (params) => {
      onProgramsChange((prev) =>
        prev.map((p) =>
          p.clientId === program.clientId
            ? { ...params, clientId: p.clientId }
            : p
        )
      )
      return Promise.resolve()
    },
    [onProgramsChange]
  )

  const handleDeleteProgram = useCallback(
    (program) => () => {
      onProgramsChange((prev) =>
        prev.filter((p) => p.clientId !== program.clientId)
      )
      return Promise.resolve()
    },
    [onProgramsChange]
  )

  const hasPrograms = programs.length > 0

  return (
    <div className="application-step3">
      {hasPrograms ? (
        <h2 className="application-programs-lead">
          Add all the programs you provide below
        </h2>
      ) : (
        <>
          <h2 className="application-programs-lead application-programs-lead--required">
            Add one or more program(s) that you provide to schools below
            <span className="application-field-required" aria-hidden="true">
              {' '}
              *
            </span>
          </h2>
          <p className="application-programs-hint">
            You need to submit at least one program to submit your application
          </p>
        </>
      )}

      <button type="button" className="button-secondary" onClick={openAddModal}>
        + Add a new program
      </button>

      {hasPrograms && (
        <div className="application-programs-list">
          <div className="application-programs-list-header">Programs</div>
          <div className="application-programs-cards">
            {/* Same wrapper as program-table.js so program-expander + / – styles apply */}
            <div className="expandable-section program-expander application-program-expander">
              {programs.map((program) => (
                <div
                  key={program.clientId}
                  className="application-program-card"
                >
                  <ProgramBlade
                    formName={`application-program-${program.clientId}`}
                    program={program}
                    onSubmit={handleUpdateProgram(program)}
                    onSubmitSuccess={() => {}}
                    onDelete={handleDeleteProgram(program)}
                    disabled={false}
                    grades={grades}
                    programTypes={programTypes}
                    outcomeTypes={outcomeTypes}
                    approachTypes={approaches}
                    disciplines={disciplines}
                    languages={languages}
                    isNewProgram={false}
                    isApplication
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showAddProgramModal && (
        <Modal onClose={closeAddModal}>
          <div className="modal-title">Add a new program</div>
          <ProgramBlade
            key={modalInstanceKey}
            formName={`application-new-program-${modalInstanceKey}`}
            isNewProgram
            onSubmit={handleAddProgram}
            onSubmitSuccess={closeAddModal}
            onDelete={closeAddModal}
            disabled={false}
            grades={grades}
            programTypes={programTypes}
            outcomeTypes={outcomeTypes}
            approachTypes={approaches}
            disciplines={disciplines}
            languages={languages}
            isApplication
          />
        </Modal>
      )}

      <ButtonArea>
        <button
          type="button"
          className="full-width button-secondary"
          disabled={!hasPrograms || applicationSubmitting || submitPending}
          onClick={() => {
            if (submitPending) return
            setSubmitPending(true)
            Promise.resolve(onSubmitApplication(programs)).finally(() => {
              setSubmitPending(false)
            })
          }}
        >
          {applicationSubmitting || submitPending
            ? 'Submitting…'
            : 'Submit Application'}
        </button>
      </ButtonArea>
    </div>
  )
}

ApplicationFormStep3.propTypes = propTypes
ApplicationFormStep3.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    disciplines: globalSelectors.definedDisciplines(state),
    programTypes: globalSelectors.orderedProgramTypes(state),
    outcomeTypes: globalSelectors.outcomeTypes(state),
    grades: globalSelectors.grades(state),
    approaches: globalSelectors.approaches(state),
    languages: globalSelectors.languages(state),
  }
}

const mapDispatchToProps = {
  fetchGrades: apiActions.fetchGrades,
  fetchOutcomeTypes: apiActions.fetchOutcomeTypes,
  fetchApproaches: apiActions.fetchApproaches,
  fetchLanguages: apiActions.fetchLanguages,
  fetchProgramTypes: apiActions.fetchProgramTypes,
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  onMount('fetchGrades'),
  onMount('fetchOutcomeTypes'),
  onMount('fetchApproaches'),
  onMount('fetchLanguages'),
  onMount('fetchProgramTypes'),
  waitFor(
    ['grades', 'outcomeTypes', 'approaches', 'languages', 'programTypes'],
    LoadingSpinner
  )
)(ApplicationFormStep3)
